<template>
  <v-form ref="form" v-model="isFormValid" @submit.prevent="onSubmit">
    <v-card v-for="section of sections">
      <v-card-title> {{ section.name }}</v-card-title>
      <v-card-text>
        <div v-for="field of section.fields">
          <EntryFieldTextComponent
            v-if="field.type == FieldTypeEnum.Text || field.type == FieldTypeEnum.Number"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue">
          </EntryFieldTextComponent>
          <EntryFieldRadioCompontent
            v-if="field.type == FieldTypeEnum.Radio"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></EntryFieldRadioCompontent>
          <EntryFieldCheckboxCompontent
            v-if="field.type == FieldTypeEnum.Checkbox"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></EntryFieldCheckboxCompontent>
          <EntryFieldSelectCompontent
            v-if="field.type == FieldTypeEnum.MultiSelect || field.type == FieldTypeEnum.Select"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue">
          </EntryFieldSelectCompontent>
          <EntryFieldDateCompontent
            v-if="field.type == FieldTypeEnum.Date"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></EntryFieldDateCompontent>
          <EntryFieldTextAreaCompontent
            v-if="field.type == FieldTypeEnum.TextArea"
            :field="field"
            :modelValue="getModelValue(field.id)"
            @update:model-value="updateModelValue"></EntryFieldTextAreaCompontent>
        </div>
      </v-card-text>
    </v-card>
    <v-btn
      v-if="!isPreview"
      color="primary"
      block
      class="rounded-lg mb-8 mt-8"
      elevation="2"
      :disabled="!isFormValid"
      @click="onSubmit"
      >Save</v-btn
    >
    <v-btn
      v-else
      color="primary"
      block
      class="rounded-lg mb-8 mt-8"
      elevation="2"
      @click="emit('close')"
      >Close</v-btn
    >
  </v-form>
</template>
<script setup lang="ts">
import EntryFieldCheckboxCompontent from '@/components/entry/fields/EntryFieldCheckboxCompontent.vue';
import EntryFieldDateCompontent from '@/components/entry/fields/EntryFieldDateCompontent.vue';
import EntryFieldRadioCompontent from '@/components/entry/fields/EntryFieldRadioCompontent.vue';
import EntryFieldSelectCompontent from '@/components/entry/fields/EntryFieldSelectCompontent.vue';
import EntryFieldTextComponent from '@/components/entry/fields/EntryFieldTextComponent.vue';
import {
  EntryDTO,
  EntrySectionDTO,
  FieldTypeEnum,
  IEntrySectionDTO,
  ISubmissionEntryFormDTO,
  IYearDTO,
  SubmissionEntryFormDTO,
  SubmissionEntryFormFieldDTO,
} from '@/dtos';
import { useEntryStore, useSubmissionnStore } from '@/stores';
import { computed, ref, watch } from 'vue';
import type { VForm } from 'vuetify/components';
import EntryFieldTextAreaCompontent from './fields/EntryFieldTextAreaCompontent.vue';

const props = defineProps<{
  year: IYearDTO;
  submissionEntryForm?: ISubmissionEntryFormDTO;
  submissionId?: string;
  isPreview?: boolean;
}>();
const emit = defineEmits(['close']);
const yearId = computed(() => props.year.id);
const entryStore = useEntryStore();
const { data } = entryStore.getEntryByYearQuery(yearId);
const entry = computed(() => data.value ?? new EntryDTO());
const submissionEntry = ref<ISubmissionEntryFormDTO>(new SubmissionEntryFormDTO());
const sections = ref<IEntrySectionDTO[]>([]);
const isFormValid = ref<boolean>(false);
const form = ref<InstanceType<typeof VForm> | null>();
const submissionStore = useSubmissionnStore();
const isPreview = computed(() => props.isPreview || false);
const updateModelValue = async (fieldId: string, value: string) => {
  let field = submissionEntry.value.fields.filter((field) => field.entryField.id == fieldId)[0];
  if (field) {
    field.value = value;
  }
};

const getModelValue = (fieldId: string) => {
  let field = submissionEntry.value.fields.filter((field) => field.entryField.id == fieldId)[0];
  if (!field) {
    field = new SubmissionEntryFormFieldDTO();
  }
  return field.value;
};
watch(
  () => entry.value.sections,
  (value: IEntrySectionDTO[]) => {
    sections.value = value.map((section) => new EntrySectionDTO(section));
  },
  { immediate: true },
);

watch(
  () => props.submissionEntryForm,
  () => {
    submissionEntry.value = new SubmissionEntryFormDTO(props.submissionEntryForm);
  },
  { immediate: true },
);

const onSubmit = async () => {
  if (!submissionEntry.value.isValid() || !props.submissionId) {
    return;
  }
  await submissionStore.updateEntry.mutateAsync({
    submissionId: props.submissionId,
    submissionEntryForm: submissionEntry.value,
  });
};
</script>
